import React from "react";
import { Button, Form, Label, Message, Modal, Select } from "semantic-ui-react";
import CustomerReviewAction from "@common/redux/actions/CustomerReviewAction";
import {
  SelectQuestion,
  TextAreaQuestion,
  YesNoQuestion,
  DateQuestion,
} from "@common/components/shared/CustomerInteractionReview/Inputs";
import useCustomerReview from "@common/components/hooks/useCustomerReview";
import { useEffect } from "react";
import { mapToSelect } from "@common/components/helpers/forms";
import useLogin from "@common/components/hooks/useLogin";
import { useSelector } from "react-redux";

const CustomerReviewFormModal = () => {
  const {
    selectedRecord,
    setReviewModalState,
    selectedReview,
    venueEnterCounts,
    venueEnterTimes,
    open,
    form: { enteredVenue, anotherReview, behaviourChanged, atRisk },
    errors,
    onCompleteClick,
    onReviewChange,
    inEditMode,
    onFormLoad,
  } = useCustomerReview();

  const { optionComplianceAllowEdit } = useSelector((state) => state.logIn) ?? {
    optionComplianceAllowEdit: true,
  };

  const [{ isDesktop }] = useLogin();

  const questions = [
    {
      name: "enteredVenue",
      question: "Has the customer entered the venue since the interaction?",
      isPrimary: true,
      Component: YesNoQuestion,
    },
    {
      name: "venueEnterCountId",
      question:
        "How many times has the customer visited the venue since this interaction?",
      isPrimary: false,
      Component: SelectQuestion,
      options: mapToSelect(venueEnterCounts),
      hidden: !enteredVenue,
    },
    {
      name: "venueEnterTimeId",
      question: "How long does the customer spend in the venue on average?",
      isPrimary: false,
      Component: SelectQuestion,
      options: mapToSelect(venueEnterTimes),
      hidden: !enteredVenue,
    },
    {
      name: "behaviourChanged",
      question:
        "Have the customer's playing habits changed since this interaction?",
      isPrimary: true,
      Component: YesNoQuestion,
    },
    {
      name: "behaviourComments",
      question: "Explain how the customer's habits have changed..",
      Component: TextAreaQuestion,
      hidden: !behaviourChanged,
      fullWidth: true,
      hideLabel: true,
    },
    {
      name: "atRisk",
      question: "Do you believe this customer is at risk of gambling harm?",
      isPrimary: true,
      Component: YesNoQuestion,
    },
    {
      name: "atRiskMeasures",
      question:
        "Explain what measures you are taking to reduce their risk of gambling harm..",
      isPrimary: true,
      Component: TextAreaQuestion,
      hidden: !atRisk,
    },
    {
      name: "anotherReview",
      question: "Set another date for review?",
      isPrimary: true,
      Component: YesNoQuestion,
    },
    {
      name: "nextReviewDate",
      question: "",
      isPrimary: true,
      Component: DateQuestion,
      fullWidth: true,
      hidden: !anotherReview,
    },
    {
      name: "barCustomer",
      question: "Bar this customer from the venue?",
      isPrimary: true,
      Component: YesNoQuestion,
    },
  ];

  const disableEditButton =
    (selectedRecord?.compliance?.review?.isClosed ?? false) ||
    optionComplianceAllowEdit === false;

  const disableEdit = disableEditButton || !inEditMode;

  useEffect(() => {
    if (open) onFormLoad();
  }, [open, selectedReview]);

  const options = selectedRecord?.compliance?.reviews ?? [];

  const mappedOptions = options.map((option) => {
    return {
      label: (
        <Label
          color={option.reviewStatus?.colour?.toLowerCase()}
          content={option.reviewStatus?.name}
        />
      ),
      text: option.reviewDate,
      value: option?.id,
    };
  });

  return (
    <Modal open={open} size="large">
      <Modal.Header>Customer Interaction Review</Modal.Header>
      <Modal.Content>
        {errors.length > 0 && (
          <Message error>
            <Message.Header>
              Please correct the following errors on your form
            </Message.Header>
            <ul>
              {errors.map((error) => (
                <li>{error}</li>
              ))}
            </ul>
          </Message>
        )}

        <Form>
          {isDesktop && options.length > 1 && (
            <Form.Field>
              <label>Select Review:</label>
              <Select
                className="w-40"
                options={mappedOptions}
                onChange={onReviewChange}
                value={selectedReview}
              />
            </Form.Field>
          )}
          {questions.map((props) => {
            const { Component, name } = props;
            return (
              <Component
                key={name}
                {...props}
                error={errors[name]}
                disabled={disableEdit}
              />
            );
          })}
        </Form>
      </Modal.Content>
      <Modal.Actions style={{ textAlign: "left" }}>
        <Button
          negative
          content="Back"
          icon="left arrow"
          onClick={() => {
            inEditMode && CustomerReviewAction.reset();
            CustomerReviewAction.dispatch({
              open: false,
              inEditMode: false,
            });
          }}
        />

        <Button
          icon={inEditMode ? "close" : "edit"}
          color="blue"
          content={inEditMode ? "Cancel" : "Edit"}
          onClick={() => {
            inEditMode && CustomerReviewAction.reset();
            setReviewModalState({ inEditMode: !inEditMode });
          }}
          disabled={disableEditButton}
        />

        <Button
          positive
          content="Complete Review"
          disabled={disableEdit}
          icon="check"
          floated="right"
          onClick={onCompleteClick}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CustomerReviewFormModal;
